import { collection, getDocs, query, where } from 'firebase/firestore';
import { callAuthenticatedFunction } from './callableService';
import {
  CASE_STATUSES,
  getDisplayName,
  MEMBERSHIP_STATUSES,
  PANEL_ROLES,
} from '../features/workspace/model';

const callWorkspaceFunction = callAuthenticatedFunction;

export const getOrganizationMemberships = async (db, userId) => {
  if (!userId) return [];
  const snapshot = await getDocs(
    query(collection(db, 'organizationMemberships'), where('userId', '==', userId))
  );
  return snapshot.docs
    .map((membershipSnapshot) => ({ id: membershipSnapshot.id, ...membershipSnapshot.data() }))
    .filter((membership) => membership.status === MEMBERSHIP_STATUSES.ACTIVE)
    .sort((left, right) => left.organizationId.localeCompare(right.organizationId));
};

export const ensureOrganizationForUser = async (db, user, profile = {}) => {
  if (!user?.uid) {
    throw new Error('A signed-in user is required to create a workspace.');
  }

  const memberships = await getOrganizationMemberships(db, user.uid);
  if (memberships.length) {
    const organizationId = memberships[0].organizationId;
    return { organizationId, memberships, created: false };
  }

  const displayName = getDisplayName(user, profile);
  const result = await callWorkspaceFunction('ensureOrganizationWorkspace', {
    organizationName: profile.organizationName?.trim() || `${displayName}'s Firm`,
    displayName,
  });
  if (!result?.organizationId) {
    throw new Error('The workspace service did not return an organization.');
  }

  // Only a workspace created specifically as this user's bootstrap organization
  // is a safe automatic destination for creator-owned legacy panels. Existing
  // memberships may span multiple firms and require an explicit migration map.
  if (result.created === true) {
    try {
      await requestLegacyWorkspaceMigration(result.organizationId);
      await requestJurorConcurrencyBackfill(result.organizationId);
    } catch (error) {
      console.warn('Legacy workspace migration is not available yet:', error?.message || error);
    }
  }

  return {
    organizationId: result.organizationId,
    memberships: result.memberships || [
      {
        organizationId: result.organizationId,
        organizationName: result.organizationName,
        role: 'owner',
        status: MEMBERSHIP_STATUSES.ACTIVE,
      },
    ],
    created: Boolean(result.created),
  };
};

export const requestLegacyWorkspaceMigration = (organizationId) =>
  callWorkspaceFunction('migrateLegacyWorkspace', { organizationId });

export const requestJurorConcurrencyBackfill = (organizationId) =>
  callWorkspaceFunction('backfillJurorConcurrencyMetadata', { organizationId });

export const repairCreatorCaseAccess = (caseId, panelId = null) =>
  callWorkspaceFunction('repairCreatorCaseAccess', {
    caseId,
    ...(panelId ? { panelId } : {}),
  });

export const createCaseWorkspace = async (
  db,
  { organizationId, user, profile = {}, caseDetails }
) => {
  if (!db || !organizationId || !user?.uid || !caseDetails?.name?.trim()) {
    throw new Error('Organization, signed-in user, and case name are required.');
  }
  return callWorkspaceFunction('createCaseWorkspace', {
    organizationId,
    caseDetails: {
      name: caseDetails.name.trim(),
      caseNumber: caseDetails.caseNumber?.trim() || '',
      county: caseDetails.county?.trim() || '',
      court: caseDetails.court?.trim() || '',
      trialDate: caseDetails.trialDate || null,
      isDemo: Boolean(caseDetails.isDemo),
      creatorDisplayName: getDisplayName(user, profile),
    },
  });
};

export const recordAuditEvent = async (
  db,
  { organizationId, caseId = null, panelId = null, actor, type, summary, metadata = {} }
) => {
  if (!db || !organizationId || !actor?.uid || !type) return null;
  return callWorkspaceFunction('recordClientAuditEvent', {
    organizationId,
    caseId,
    panelId,
    type,
    summary: summary || type,
    metadata,
  });
};

export const archiveCaseWorkspace = async (db, caseRecord, actor) => {
  if (!db || !caseRecord?.id || !actor?.uid) {
    throw new Error('A signed-in case member is required.');
  }
  return callWorkspaceFunction('setCaseLifecycle', {
    caseId: caseRecord.id,
    status: CASE_STATUSES.ARCHIVED,
  });
};

export const restoreCaseWorkspace = async (db, caseRecord, actor) => {
  if (!db || !caseRecord?.id || !actor?.uid) {
    throw new Error('A signed-in case member is required.');
  }
  return callWorkspaceFunction('setCaseLifecycle', { caseId: caseRecord.id, status: 'restore' });
};

export const joinPanelBySessionCode = (code) =>
  callWorkspaceFunction('joinPanelBySessionCode', { code });
export const createPanelInvitation = ({ panelId, email = '', role = PANEL_ROLES.COLLABORATOR }) =>
  callWorkspaceFunction('createPanelInvitation', { panelId, email, role });
export const acceptPanelInvitation = (token) =>
  callWorkspaceFunction('acceptPanelInvitation', { token });
export const createOrganizationInvitation = (payload) =>
  callWorkspaceFunction('createOrganizationInvitation', payload);
export const acceptOrganizationInvitation = (token) =>
  callWorkspaceFunction('acceptOrganizationInvitation', { token });
export const revokeOrganizationInvitation = (invitationId) =>
  callWorkspaceFunction('revokeOrganizationInvitation', { invitationId });
export const updateOrganizationMemberRole = (organizationId, userId, role) =>
  callWorkspaceFunction('updateOrganizationMemberRole', { organizationId, userId, role });
export const setOrganizationMemberStatus = (organizationId, userId, status) =>
  callWorkspaceFunction('setOrganizationMemberStatus', { organizationId, userId, status });
export const transferOrganizationOwnership = (organizationId, userId) =>
  callWorkspaceFunction('transferOrganizationOwnership', { organizationId, userId });
export const assignCaseMember = (caseId, userId, role) =>
  callWorkspaceFunction('assignCaseMember', { caseId, userId, role });
export const removeCaseMember = (caseId, userId) =>
  callWorkspaceFunction('removeCaseMember', { caseId, userId });
export const updatePanelMemberRole = (panelId, userId, role) =>
  callWorkspaceFunction('updatePanelMemberRole', { panelId, userId, role });
export const removePanelMember = (panelId, userId) =>
  callWorkspaceFunction('removePanelMember', { panelId, userId });
export const rotateSessionCode = (panelId) =>
  callWorkspaceFunction('rotateSessionCode', { panelId });
export const revokeSessionCode = (panelId) =>
  callWorkspaceFunction('revokeSessionCode', { panelId });
export const archivePanel = (panelId) => callWorkspaceFunction('archivePanel', { panelId });
export const setCaseLifecycleStatus = (caseId, status) =>
  callWorkspaceFunction('setCaseLifecycle', { caseId, status });
export const requestPermanentCaseDeletion = (caseId, confirmCaseName) =>
  callWorkspaceFunction('requestPermanentCaseDeletion', { caseId, confirmCaseName });
export const authorizePanelExport = (panelId, format = 'json') =>
  callWorkspaceFunction('authorizePanelExport', { panelId, format });
export const authorizePanelImport = (panelId, format, jurorCount = 0) =>
  callWorkspaceFunction('authorizePanelImport', { panelId, format, count: jurorCount });
export const createCheckoutSession = (organizationId, interval, extraSeats = 0) =>
  callWorkspaceFunction('createCheckoutSession', { organizationId, interval, extraSeats });
export const createCustomerPortalSession = (organizationId) =>
  callWorkspaceFunction('createCustomerPortalSession', { organizationId });
export const updateSubscriptionSeats = (organizationId, extraSeats) =>
  callWorkspaceFunction('updateSubscriptionSeats', { organizationId, extraSeats });
export const bootstrapLocalDevelopmentAccount = (organizationId = null) =>
  callWorkspaceFunction('bootstrapLocalDevelopmentAccount', { organizationId });
export const getBillingConfigurationStatus = (organizationId) =>
  callWorkspaceFunction('getBillingConfigurationStatus', { organizationId });
export const createQuestionBank = (payload) =>
  callWorkspaceFunction('createQuestionBank', payload);
export const updateQuestionBank = (payload) =>
  callWorkspaceFunction('updateQuestionBank', payload);
export const duplicateQuestionBank = (bankId, name = null) =>
  callWorkspaceFunction('duplicateQuestionBank', { bankId, ...(name ? { name } : {}) });
export const archiveQuestionBank = (bankId) =>
  callWorkspaceFunction('archiveQuestionBank', { bankId });
export const restoreQuestionBankVersion = (bankId, version, expectedVersion) =>
  callWorkspaceFunction('restoreQuestionBankVersion', { bankId, version, expectedVersion });
export const applyQuestionBanksToPanel = (
  panelId,
  bankVersions,
  expectedPanelRevision,
  mode = 'merge',
  conflictResolutions = {},
  starterId = null
) =>
  callWorkspaceFunction('applyQuestionBanksToPanel', {
    panelId,
    bankVersions,
    expectedPanelRevision,
    mode,
    conflictResolutions,
    ...(starterId ? { starterId } : {}),
  });
export const updatePanelQuestionSet = (panelId, questionSet, expectedRevision) => {
  const content = questionSet?.content || questionSet || {};
  const starterId = questionSet?.starterId || content?.starterId || null;
  const clearSources = questionSet?.clearSources === true || content?.clearSources === true;
  return callWorkspaceFunction('updatePanelQuestionSet', {
    panelId,
    ...(starterId
      ? {
          starterId,
          mode: questionSet?.mode || content?.mode || 'replace',
          conflictResolutions:
            questionSet?.conflictResolutions || content?.conflictResolutions || {},
        }
      : {
          sections: content.sections || [],
          questions: content.questions || [],
          ...(clearSources ? { clearSources: true } : {}),
        }),
    expectedRevision,
  });
};
export const savePanelQuestionSetAsBank = (panelId, payload) =>
  callWorkspaceFunction('savePanelQuestionSetAsBank', { panelId, ...payload });
export const saveDraftSetupStep = (caseId, panelId, step, payload, expectedPanelVersion) =>
  callWorkspaceFunction('saveDraftSetupStep', {
    caseId,
    panelId,
    step,
    payload,
    expectedPanelVersion,
  });
export const finalizeCaseSetup = (
  caseId,
  panelId,
  targetStatus = CASE_STATUSES.READY,
  acknowledgedWarnings = []
) => callWorkspaceFunction('finalizeCaseSetup', {
  caseId,
  panelId,
  targetStatus,
  acknowledgedWarnings,
});
